import { Component, EventEmitter, OnInit, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { EmailValidator, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import {Message, OverlayPanel, SelectItem} from 'primeng/primeng';

//constants

import * as enums from '../../enums/index'
import * as ent from '../../entities/index'
import * as filters from '../../filters/index'
import * as serv from '../../services/index'
import * as util from '../../util/index'

//validators
import { dropDownValidator, emailValidatorWithParameters, phoneNumberValidatorWithParameters }                from '../../validators/CustomValidators';
import { hiddenDropDownValueValidator }                from '../../validators/CustomValidators';
import { postalCodeValidator }                from '../../validators/CustomValidators';
import { emailValidator }                from '../../validators/CustomValidators';

//components
import {StateDropdownComponent} from '../../components/DomainData/StateDropdown.component';
import { CustomersAutoCompleteComponent } from '../Customers/CustomersAutoComplete.component';

//constants
import { Constants }         from '../../constants/Constants';

//base
import { BaseControlGroupComponent }         from '../../baseclasses/BaseControlGroup.component';
import { MapComponent } from '../../components/Map/Map.component';
import { SelectPayerComponent } from '../../components/Customers/SelectPayer.component';
import { MessageHandlerComponent } from '../Controls/MessageHandler.component';
import { BlockComponent } from '../Controls/Block.component';
import { ContactGridComponent } from '../Grids/ContactGrid.component';


@Component({
  selector: 'my-billingaddress',
  templateUrl: './BillingAddress.component.html',
  providers: [{provide: BaseControlGroupComponent, useExisting: forwardRef(() => BillingAddressComponent)}]
})
export class BillingAddressComponent extends BaseControlGroupComponent {

    addressForm: FormGroup;
    billingInfo: ent.BillingInfo = new ent.BillingInfo();
    customerOwnershipID: number;
    requestUtil: util.RequestUtil;
    disableAddressFormFields: boolean;
    unknownPayer: boolean = false;
    payerNotAvailableFromMirrorCustomer: boolean = false;
    accountGroupPayer: string = Constants._ACCOUNT_GROUP_PAYER;
    accountGroupSoldTo: string = Constants._ACCOUNT_GROUP_SOLD_TO;
    distributionChannel: string = Constants._DISTRIBUTION_CHANNEL_DSD;
    displayConfirmPayerDialog: boolean = false;
    selectedPayer: ent.Customer;
    isContactGridHidden: boolean = true;
    showEditPayerContactsButton: boolean;
    
    @Output() public billingAddressComponentChange: EventEmitter<ent.Address> = new EventEmitter();
    @Output() public copyOverallAccountPhoneNumberCopied: EventEmitter<boolean> = new EventEmitter();
    @Output() public copyOverallAccountEmailCopied: EventEmitter<boolean> = new EventEmitter();
    @Output() public onSelectEditPayerContactClicked: EventEmitter<boolean> = new EventEmitter();

    @Input() public showEmailInvoices: boolean;
    @Input() public showUnknownPayer: boolean;
    @Input() public showPayerNotAvailableFromMirrorCustomer: boolean;
    @Input() public showAddressLookup: boolean;
    @Input() public showStreetNumber: boolean;
    @Input() public requestTypeID: number;
    @Input() public showContactLabel: boolean;
    @Input() public showEditPayerContacts: boolean;
    @Input() public showMobilePhone: boolean;


    @ViewChild(StateDropdownComponent) public stateDropdownComponent: StateDropdownComponent;
    @ViewChild('payerAC') public payerAC: CustomersAutoCompleteComponent;
    @ViewChild('soldToAC') public soldToAC: CustomersAutoCompleteComponent;
    @ViewChild(MapComponent) public mapComponent: MapComponent;
    @ViewChild(SelectPayerComponent) selectPayerComponent: SelectPayerComponent;
    @ViewChild(ContactGridComponent) contactGridComponent: ContactGridComponent;
    @ViewChild(MessageHandlerComponent) messageHandlerComponent: MessageHandlerComponent;
    @ViewChild(BlockComponent) blockComponent: BlockComponent;

    contactValidations: ent.ContactValidation[] = [];

    setDates(){}

    setRequestInfo(request: ent.Request){
        this.request = request;
        this.billingInfo = request.BillingInfo;
        this.initializeBillingAddress();
        this.requestUtil.populateCustomerAutoComplete(this.payerAC, this.billingInfo.PayerNumber);
        this.requestUtil.populateCustomerAutoComplete(this.soldToAC, this.billingInfo.MirrorCustomerNumber);

        this.unknownPayerChange(this.billingInfo.UnknownPayer, false);
        this.payerNotAvailableFromMirrorCustomerChange(this.billingInfo.PayerNotAvailableFromMirrorCustomer, false);
        if (request.BillingAddress && util.AddressUtil.isAddressPopulated(request.BillingAddress)){
            this.billingInfo.Address = request.BillingAddress;
        }
    }

    constructor(fb: FormBuilder, private customerService: serv.CustomerService) 
    { 
        
        super();
        this.requestUtil = new util.RequestUtil(this.customerService);

        let group = {
          'businessName' : '',
          'attentionToContact' : '',
          'streetNumber' : '',
          'streetName' : '',
          'supplemental' : '',
          'city' : '',
          'state' : '',
          'postalCode' : '',
          'poBoxNumber' : '',
          'telephone' : '',
          'telephoneExtension' : '',
          'mobilePhone' : '',
          'fax' : '',
          'email' : '',
          'billingAddressSameAsDeliveryAddress' : '',
          'emailInvoices' : '',
          'payerNumber' : '',
          'soldToMirror' : '',
          'unknownPayer' : '',
          'payerNotAvailableFromMirrorCustomer' : ""
        };
        
        this.addressForm = fb.group(group);

    }

    ngOnInit(): void {

        this.domainDataService.getContactValidations({ IncludeInactive: false }).subscribe(x=>{
            this.contactValidations = x;
            
            var emailValidators = [emailValidatorWithParameters(this.contactValidations), emailValidator];
            
            this.addressForm.controls.email.setValidators(emailValidators);

            this.addressForm.controls.telephone.setValidators(phoneNumberValidatorWithParameters(this.contactValidations));
        });
        
        this.initializeBillingAddress();

        this.addressForm.controls.postalCode.setValidators(postalCodeValidator);
    }

    ngAfterViewInit(){
        this.contactGridComponent.recordEditComponent.hostedBy = Constants._HOSTED_BY_BILLING_ADDRESS;
    }

    initializeBillingAddress(){
        this.billingInfo.Address = new ent.Address();
        this.billingInfo.Address.AddressTypeID = enums.AddressType.Billing;
    }

    isFieldValid(fieldName: string){
        var val = true;
        var form = this.addressForm;
        if (form.controls[fieldName].errors){
            if (form.controls[fieldName].errors.invalid){
                val = false;
            }
        }
        return val;
    }

    getCustomError(fieldName: string) : string{
        
        if (!this.isFieldValid(fieldName)) {
            return "";
        }
        var form = this.addressForm;
        if (form.controls[fieldName].errors){
            if (form.controls[fieldName].errors.customError){
                return form.controls[fieldName].errors.customError;
            }
        }
        return "";
    }

    stateChange(event: any){
        this.billingInfo.Address.StateID = event;
    }

    disableField() : string{
        if (this.shouldDisable()){
            return 'disabled';
        }
        return null;

    }
    disableControl() : boolean{
        if (this.shouldDisable()){
            return true;
        }
        return false;
    }
    shouldDisable(): boolean{
        if (this.request && this.request.RequestStatusID == enums.RequestStatusEnum.Rejected) {
            return false;
        }
        return (this.customerOwnershipID != undefined && this.customerOwnershipID == enums.CustomerOwnershipEnum.Corporate) 
            || this.billingInfo.BillingAddressSameAsDeliveryAddress;
    }

    handleCustomerOwnershipChange(customerOwnershipID: number) {
        this.customerOwnershipID = customerOwnershipID;
        if (this.shouldDisable()) {
            // this.billingInfo.BusinessName = "";
            // this.billingInfo.Email = "";
            // this.billingInfo.PhoneNumber = "";
            // this.billingInfo.PhoneNumberExtension = "";
            // this.billingInfo.Fax = "";
            // this.billingInfo.MobilePhone = "";
            // this.billingInfo.AttentionToContact = "";
            // this.stateDropdownComponent.value = Constants._NONE;
            // this.billingInfo.Address.State = null;
            // this.billingInfo.Address.City = "";
            // this.billingInfo.Address.POBoxNumber = "";
            // this.billingInfo.Address.PostalCode = "";
            // this.billingInfo.Address.StreetName = "";
            // this.billingInfo.Address.StreetNumber = "";
            // this.billingInfo.Address.Supplemental = "";
        }
    }

    selectedPayerNumberChanged(customerNumbers: string[]) {
        if (customerNumbers && customerNumbers.length > 0) {
            let customerNumber = customerNumbers[customerNumbers.length - 1];
            var filter: filters.CustomerFilter = { CustomerNumber: customerNumber, PYPayerNumber: customerNumber };
            this.customerService.getCustomers(filter).subscribe(x => {
                if (x && x.length > 0) {
                    this.selectedPayer = x[0];
                    this.displayConfirmPayerDialog = true;
                    this.billingInfo.PayerNumber = x[0].PYPayerNumber;
                    this.payerAC.msgs = [];
                }
                else {
                    this.billingInfo.PayerNumber = "";
                }
            });
        }
        else {
            this.billingInfo.PayerNumber = "";
            this.payerAC.msgs = [];
        }
    }

    noPayerMatchesFound() {
        this.payerAC.msgs = [];
        this.payerAC.msgs.push({severity:'error', summary:'', detail:'The number entered is not a payer. Please try again or use one of the other payer linkage options below.'});
        this.payerAC.msgs.push({severity:'error', summary:'', detail:'To view the next option, please click the Payer Unknown checkbox.'});
    }

    selectedSoldToMirrorChanged(customerNumbers: string[]) {
        if (customerNumbers && customerNumbers.length > 0) {
            let customerNumber = customerNumbers[customerNumbers.length - 1];
            var filter: filters.CustomerFilter = { CustomerNumber: customerNumber };
            this.customerService.getCustomers(filter).subscribe(x => {
                if (x && x.length > 0) {
                    var customer = x[0];
                    if (customer.PYPayerNumber == customer.CustomerNumber) {
                        this.soldToAC.msgs = [];
                        this.soldToAC.msgs.push({severity:'error', summary:'', detail:'The account to mirror is a sold to account without a payer record. '});
                        this.soldToAC.msgs.push({severity:'error', summary:'', detail:'Therefore a new payer will need to be created for this new sold to account being requested. '});
                        this.soldToAC.msgs.push({severity:'error', summary:'', detail:'Please try again or proceed to filling out the billing address section.  '});
                        
                    }
                    else {
                        this.billingInfo.MirrorCustomerNumber = customer.CustomerNumber;
                        this.billingInfo.PayerNumber = customer.PYPayerNumber;

                        var filter2: filters.CustomerFilter = { CustomerNumber: customer.PYPayerNumber };

                        this.customerService.getCustomers(filter2).subscribe(y => {
                            if (y && y.length > 0) {
                                var payerCustomer = y[0];
                                this.selectedPayer = payerCustomer;
                                this.displayConfirmPayerDialog = true;
                            }
                        });
                        this.requestUtil.populateCustomerAutoComplete(this.payerAC, this.billingInfo.PayerNumber);
                        this.soldToAC.msgs = [];
                    }
                }
                else {
                    this.billingInfo.MirrorCustomerNumber = "";
                }
            });
        }
        else {
            this.billingInfo.MirrorCustomerNumber = "";
            this.soldToAC.msgs = [];
        }
    }

    noSoldToMirrorMatchesFound() {
        this.soldToAC.msgs = [];
        this.soldToAC.msgs.push({severity:'error', summary:'', detail:'The number entered is not a Sold To Customer. Please try again or use one of the other payer '});
        this.soldToAC.msgs.push({severity:'error', summary:'', detail:'linkage options below. '});
        this.soldToAC.msgs.push({severity:'error', summary:'', detail:'To view the next option, please click the Payer not Available from Account to Mirror checkbox.'});
    }
    

    

    filteringAddress(event: any) {
        this.disableAddressFormFields = true;
    }

    filteredAddress(event: any) {
        this.disableAddressFormFields = false;
    }

    disableAddressInputs(): string {
        if (this.disableAddressFormFields) {
            return 'disabled';
        }
        return null;
    }

    addressChanged(address: ent.MyAddress) {
        this.billingInfo.Address.StreetNumber = address.HouseNumber;
        this.billingInfo.Address.StreetName = address.StreetName;
        this.billingInfo.Address.City = address.City;
        this.billingInfo.Address.PostalCode = address.PostalCode;
        this.billingInfo.Address.Latitude = address.Lat;
        this.billingInfo.Address.Longitude = address.Lng;

        if (address.State) {
            var selectedState = this.stateDropdownComponent.states.filter(x => x.Code.toUpperCase() == address.State.toUpperCase())[0];
            this.stateDropdownComponent.value = selectedState.ID;
            this.billingInfo.Address.State = selectedState;
        } else {
            this.stateDropdownComponent.value = Constants._NONE;
            this.billingInfo.Address.State = null;
        }
        

        this.mapComponent.clearMarkers();
        if (address && address.Lat && address.Lng) {
            this.mapComponent.addLocationToMap(address, null);
        }
        

        this.disableAddressFormFields = false;

    }

    streetNameChanged(){
        this.billingAddressComponentChange.emit(this.billingInfo.Address);
    }
    
    cityChanged(){
        this.billingAddressComponentChange.emit(this.billingInfo.Address);
    }

    stateChanged(){
        this.billingAddressComponentChange.emit(this.billingInfo.Address);
    }

    postalCodeChanged(){
        this.billingAddressComponentChange.emit(this.billingInfo.Address);
    }

    onPayerSearchClick(event: any) {
        var stateCode: string = "";
        if (this.billingInfo.Address.State) {
            stateCode = this.billingInfo.Address.State.Code;
        }

        var request: ent.GetPayersRequest = {
            BusinessName: this.billingInfo.BusinessName,
            StreetNumber: this.billingInfo.Address.StreetNumber,
            StreetName: this.billingInfo.Address.StreetName,
            City: this.billingInfo.Address.City,
            State: stateCode,
            PostalCode: this.billingInfo.Address.PostalCode,
            POBoxNumber: this.billingInfo.Address.POBoxNumber,
            Latitude: this.billingInfo.Address.Latitude,
            Longitude: this.billingInfo.Address.Longitude
        };

        this.selectPayerComponent.customerGridComponent
            .onCustomerSelected.subscribe((customer: ent.Customer) => this.selectPayerComponent.customerSelected(customer));
        
        this.blockComponent.blocked = true;
        this.selectPayerComponent.getPayers(request).subscribe(x=>{
            this.blockComponent.blocked = false;
            this.selectPayerComponent.displayDialog = true;
        });
        
    }

    onSelectEditPayerContact(event: any){
        this.onSelectEditPayerContactClicked.emit(true);
    }
    
    onSelectPayer(event: any) {
        
        this.selectPayerComponent.displayDialog = false;
    }

    onSelectPayerCancel(event: any) {
        this.selectPayerComponent.displayDialog = false;
    }

    onPayerSelected(payer: ent.Customer) {
        this.billingInfo.PayerNumber = payer.PYPayerNumber;
        this.requestUtil.populateCustomerAutoComplete(this.payerAC, this.billingInfo.PayerNumber);
        this.selectPayerComponent.displayDialog = false;
    }

    unknownPayerChange(checked: boolean, doClear: boolean) {
        this.unknownPayer = checked;
        if (checked == true && doClear == true) {
            this.billingInfo.PayerNumber = "";
            this.payerAC.clear();
        }
        
    }

    payerNotAvailableFromMirrorCustomerChange(checked: boolean, doClear: boolean) {
        this.payerNotAvailableFromMirrorCustomer = checked;
        if (checked == true && doClear == true) {
            this.billingInfo.PayerNumber = "";
            this.payerAC.clear();
            this.billingInfo.MirrorCustomerNumber = "";
            this.soldToAC.clear();
        }
    }
    
    showAccountToMirror(): boolean{
        return this.unknownPayer;
    }

    showPayerLookup(): boolean{
        return this.unknownPayer && this.payerNotAvailableFromMirrorCustomer;
    }

    isSoldTo(): boolean {
        return this.requestTypeID == enums.RequestTypeEnum.SoldToCustomer;
    }

    onHideConfirmPayerDialog(event: any) {
        this.displayConfirmPayerDialog = false;
    }

    onConfirmPayerYesClick(event: any) {
        this.displayConfirmPayerDialog = false;
    }

    onConfirmPayerCancelClick(event: any) {
        
        this.displayConfirmPayerDialog = false;
        this.payerAC.clear();
        this.billingInfo.PayerNumber = "";
        this.soldToAC.clear();
        this.billingInfo.MirrorCustomerNumber = "";
    }

    showContactGrid(val: boolean){
        //wired and functional method, but deprecated
        this.isContactGridHidden = val;
    }
    

}